import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, CheckCircle2, Ban, Send, Upload, Paperclip, MessageSquare, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, PriorityBadge, ApprovalBadge, ProgressBar } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("dasarro_token");

export default function TaskWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => api.get(`/tasks/${id}`).then((r) => setT(r.data)).catch(() => navigate("/tasks")), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.post(`/tasks/${id}/update`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleCheck = async (i) => { await api.post(`/tasks/${id}/checklist?index=${i}`); load(); };
  const submitApproval = async () => {
    try { await api.post(`/tasks/${id}/submit-approval`); toast.success("Submitted for approval"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/tasks/${id}/comment`, { text: comment }); setComment(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/tasks/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  if (!t) return null;
  const Section = ({ title, icon: Icon, children }) => (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3"><Icon size={13} /> {title}</div>
      {children}
    </div>
  );

  return (
    <div>
      <PageHeader title={t.title} subtitle={`${t.ref || ""} · ${t.brand} · ${t.division}`}
        actions={<button data-testid="back-to-tasks" onClick={() => navigate("/tasks")} className="flex items-center gap-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2 rounded-sm transition-colors"><ArrowLeft size={15} /> Tasks</button>} />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={t.status} /><PriorityBadge priority={t.priority} /><ApprovalBadge status={t.approval_status} />
            <div className="flex items-center gap-2 min-w-[160px]"><ProgressBar value={t.progress} /><span className="text-xs font-mono text-zinc-500">{t.progress}%</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="task-start" onClick={() => setStatus("In Progress")} className="flex items-center gap-1.5 text-xs bg-white text-black font-medium px-3 py-2 rounded-sm hover:bg-zinc-200"><Play size={13} /> Start</button>
            <button data-testid="task-pause" onClick={() => setStatus("On Hold")} className="flex items-center gap-1.5 text-xs border border-white/15 text-zinc-200 px-3 py-2 rounded-sm hover:bg-white/5"><Pause size={13} /> Pause</button>
            <button data-testid="task-block" onClick={() => setStatus("Blocked")} className="flex items-center gap-1.5 text-xs border border-white/15 text-[#FF6259] px-3 py-2 rounded-sm hover:bg-white/5"><Ban size={13} /> Blocked</button>
            <button data-testid="task-submit-approval" onClick={submitApproval} className="flex items-center gap-1.5 text-xs border border-white/15 text-[#C77DFF] px-3 py-2 rounded-sm hover:bg-white/5"><Send size={13} /> Submit for Approval</button>
            <button data-testid="task-complete" onClick={() => setStatus("Completed")} className="flex items-center gap-1.5 text-xs border border-[#00FF66]/40 text-[#00FF66] px-3 py-2 rounded-sm hover:bg-white/5"><CheckCircle2 size={13} /> Mark Complete</button>
          </div>

          {t.description && <Section title="Description" icon={MessageSquare}><p className="text-sm text-zinc-300 whitespace-pre-wrap">{t.description}</p></Section>}

          <Section title="Checklist" icon={Check}>
            <div className="space-y-1.5">
              {(t.checklist || []).map((c, i) => (
                <button key={i} data-testid={`checklist-${i}`} onClick={() => toggleCheck(i)} className="w-full flex items-center gap-2 text-left text-sm py-1 group">
                  <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${c.done ? "bg-[#00FF66] border-[#00FF66]" : "border-white/25"}`}>{c.done && <Check size={11} className="text-black" />}</span>
                  <span className={c.done ? "text-zinc-500 line-through" : "text-zinc-300"}>{c.label}</span>
                </button>
              ))}
              {(t.checklist || []).length === 0 && <span className="text-xs text-zinc-600">No checklist items.</span>}
            </div>
          </Section>

          <Section title="Files & Attachments" icon={Paperclip}>
            <label className="inline-flex items-center gap-2 text-xs border border-white/15 text-zinc-200 px-3 py-2 rounded-sm hover:bg-white/5 cursor-pointer mb-3">
              <Upload size={13} /> Upload file
              <input type="file" className="hidden" data-testid="task-file-input" onChange={upload} />
            </label>
            <div className="space-y-1.5">
              {(t.files || []).map((f) => (
                <a key={f.id} data-testid={`file-${f.id}`} href={`${BACKEND}/api/files/${f.storage_path}?auth=${token()}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-[#4DA3FF] hover:underline"><Paperclip size={12} /> {f.filename}</a>
              ))}
              {(t.files || []).length === 0 && <span className="text-xs text-zinc-600">No files uploaded.</span>}
            </div>
          </Section>

          <Section title="Comments" icon={MessageSquare}>
            <div className="flex gap-2 mb-3">
              <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Write a comment…" className="flex-1 bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white" />
              <button data-testid="comment-submit" onClick={addComment} className="text-xs bg-white text-black font-medium px-3 rounded-sm hover:bg-zinc-200">Post</button>
            </div>
            <div className="space-y-3">
              {(t.comments || []).slice().reverse().map((c) => (
                <div key={c.id} className="text-sm">
                  <div className="flex items-center gap-2"><span className="font-medium text-zinc-200">{c.by}</span><span className="text-[10px] font-mono text-zinc-600">{(c.at || "").slice(0, 16).replace("T", " ")}</span></div>
                  <p className="text-zinc-400">{c.text}</p>
                </div>
              ))}
              {(t.comments || []).length === 0 && <span className="text-xs text-zinc-600">No comments yet.</span>}
            </div>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Details" icon={Clock}>
            <dl className="space-y-2 text-sm">
              {[["Assigned to", t.assigned_to], ["Assigned by", t.assigned_by], ["Approver", t.approver], ["Due date", t.due_date], ["Platform", t.platform], ["Work type", t.work_type]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3"><dt className="text-zinc-600">{k}</dt><dd className="text-zinc-300 text-right">{v}</dd></div>
              ))}
            </dl>
          </Section>
          <Section title="Activity Timeline" icon={Clock}>
            <div className="space-y-3">
              {(t.activity || []).map((a, i) => (
                <div key={i} className="relative pl-4 border-l border-white/10">
                  <span className="absolute -left-[3px] top-1.5 w-1.5 h-1.5 rounded-full bg-zinc-500" />
                  <div className="text-xs text-zinc-300">{a.action}</div>
                  <div className="text-[10px] font-mono text-zinc-600">{a.by} · {(a.at || "").slice(0, 16).replace("T", " ")}</div>
                  {a.note && <div className="text-[11px] text-zinc-500 mt-0.5">{a.note}</div>}
                </div>
              ))}
              {(t.activity || []).length === 0 && <span className="text-xs text-zinc-600">No activity yet.</span>}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
